import { useState } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogIn, AlertCircle, ShieldCheck } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, mfaRequired ? totpCode : null);
      if (result?.requires_mfa) {
        setMfaRequired(true);
        setTotpCode('');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm rounded-2xl border shadow-lg">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="mx-auto mb-4">
            <img src="/logo-login.png" alt="constructOS" className="h-10 mx-auto object-contain" />
          </div>
          <CardTitle className="sr-only">constructOS</CardTitle>
          <p className="text-xs text-slate-400 mt-1">
            {mfaRequired ? 'Verificación de dos pasos' : 'Inicia sesión para continuar'}
          </p>
        </CardHeader>
        <CardContent className="px-6 pb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!mfaRequired ? (
              <>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" required autoFocus data-testid="login-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Contraseña</label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <ShieldCheck className="h-8 w-8 text-[#F05A28]" />
                </div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Código de Google Authenticator</label>
                <Input
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000" required autoFocus
                  className="text-center text-2xl tracking-[0.3em] font-mono"
                  data-testid="login-totp"
                />
                <button type="button" onClick={() => { setMfaRequired(false); setTotpCode(''); setError(''); }} className="text-[11px] text-slate-400 hover:text-slate-600 mt-2 block mx-auto">
                  Volver al login
                </button>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}
            <Button type="submit" className="w-full h-10" disabled={loading} data-testid="login-submit">
              {mfaRequired ? <ShieldCheck className="h-4 w-4 mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
              {loading ? 'Verificando...' : mfaRequired ? 'Verificar Código' : 'Iniciar Sesión'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
